/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: AI Product Avatar features section showcasing key features with alternating layout
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

interface FeatureItemProps {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
  buttonText?: string;
  buttonHref?: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  title,
  description,
  imageUrl,
  imageAlt,
  reverse = false,
  buttonText = 'Create Your Product Avatar',
  buttonHref,
}) => {
  return (
    <div
      className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-6 pad:gap-8 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full flex flex-col items-start">
        <h3 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
          {title}
        </h3>
        <p className="text-sm pad:text-base sm:text-base text-gray-600 mb-4 pad:mb-6 leading-relaxed">
          {description}
        </p>
        <StartButton text={buttonText} href={buttonHref} />
      </div>
    </div>
  );
};

const PowerfulFeaturesSection: React.FC = () => {
  const features = [
    {
      title: 'Find the Right Face for Your Product',
      description:
        "When you need a presenter fast, FalcoCut gives you 100+ ready-made AI presenters and model photos to choose from. You can match the style, age, or vibe you want without hiring anyone. Your videos look polished from the start, even when you're moving quickly. With so many options, you can test different looks and see which one your audience connects with most. And because everything is ready to use, you can produce a steady flow of high-quality ad creatives without planning shoots or managing talent.",
      imageUrl:
        'https://assets.falcocut.ai/ai-product-avatar/find-the-right-face-for-your-product.webp',
      imageAlt: 'Find the right face for your product with 100+ AI presenters',
    },
    {
      title: 'Build a Digital Twin for Your Brand',
      description:
        "Turn your own photo into a speaking avatar that carries your brand's look and personality wherever it appears. It's an easy way to keep the same face across social posts, product pages, and short UGC-style videos without filming yourself each time. Your digital twin can show up in reviews, promos, and branded clips, giving your brand content a steady, familiar presence. With just one image, you can run cross-platform UGC campaigns that feel personal, on-brand, and effortless to scale.",
      imageUrl:
        'https://assets.falcocut.ai/ai-product-avatar/build-a-digital-twin-for-your-brand.webp',
      imageAlt: 'Build a digital twin for your brand',
    },
    {
      title: 'Interact with Your Product Naturally',
      description:
        'With a single product photo, you can create a video that shows what your item looks like and why it matters. Your avatar can hold, point to, and showcase your product with realistic hand movements that feel like true UGC. FalcoCut blends your product image into the scene with proper proportions and lighting, so it looks like it was filmed together. You can generate different poses and angles to highlight key features, helping viewers understand the product faster and stay engaged longer across ads and product pages.',
      imageUrl:
        'https://assets.falcocut.ai/ai-product-avatar/interact-with-your-product-naturally.webp',
      imageAlt: 'Interact with your product naturally',
    },
    {
      title: 'Turn Scripts Into Spoken Video',
      description:
        'Add your script by typing, pasting, or uploading audio, and FalcoCut brings it to life with smooth timing, natural expression. The system can mirror your speaking style by voice cloning and generate accurate lip-sync, making the performance feel authentic on screen. Updating lines for new versions or quick tests only takes a moment, so you can refine messages anytime. With one script powering multiple videos, teams can scale content quickly while keeping the voice and tone consistent across campaigns.',
      imageUrl:
        'https://assets.falcocut.ai/ai-product-avatar/turn-scripts-into-spoken-video.webp',
      imageAlt: 'Turn scripts into spoken video',
    },
    {
      title: 'Generate Expressive Voiceovers',
      description:
        "Your virtual presenters can speak in 100+ hyper-real AI voices, so you can match the sound to the type of video you're creating—whether it's a quick UGC ad, a product explainer, or a branded announcement. Adjust the pace and emotion to fit different moments, giving every clip a polished, expressive feel without recording sessions. With so many voice options and styles, every product story can sound consistent, professional, and ready for any campaign.",
      imageUrl:
        'https://assets.falcocut.ai/ai-product-avatar/generate-expressive-voiceovers.webp',
      imageAlt: 'Generate expressive voiceovers with 100+ AI voices',
    },
    {
      title: 'Speak to Global Audiences',
      description:
        'Reach international shoppers with fast, accurate translation and voiceover capabilities in 30+ languages and accents. Your content can be localized instantly while staying clear, natural, and culturally aligned with each region. Choose from a wide range of AI voices or use voice cloning to maintain the same speaking style across every market. Teams can scale multilingual product demos, launch videos, and educational content without managing extra voice talent or re-recording sessions.',
      imageUrl:
        'https://assets.falcocut.ai/ai-product-avatar/speak-to-global-audiences.webp',
      imageAlt: 'Speak to global audiences in 30+ languages',
    },
    {
      title: 'Make Your Video Match Your Style',
      description:
        "FalcoCut lets you shape the look and feel of your product video so it fits your brand and speaks to your audience. Pick subtitle styles that work for different needs—clean captions for Amazon listings, bold text for TikTok, or friendly rounded subtitles for lifestyle content. Choose backgrounds that match your product's context, like a white studio, a cozy home scene, or a branded color wall. These options help you create videos that feel intentional and on-brand, making your product easier to understand and more appealing at a glance.",
      imageUrl:
        'https://assets.falcocut.ai/ai-product-avatar/make-your-video-match-your-style.webp',
      imageAlt: 'Make your video match your style',
    },
    {
      title: 'Ready for Every Channel',
      description:
        "Adapt your product video to any platform by choosing the aspect ratio that fits each channel. Turn one clip into a vertical format for TikTok, a square version for Instagram, or a widescreen demo for YouTube and your online store. Your content displays correctly and feels native wherever it appears, making it easier for shoppers to watch without friction. By matching each platform's viewing habits, your videos gain stronger visibility and attract more engaged viewers.",
      imageUrl:
        'https://assets.falcocut.ai/ai-product-avatar/ready-for-every-channel.webp',
      imageAlt: 'Ready for every channel with multiple aspect ratios',
    },
  ];

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Powerful Features for Better Product Avatars
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          Discover how FalcoCut combines AI product avatars, voice tools, and flexible
          creation options to help you produce clear, expressive product videos with ease.
          It brings all your product visuals and messages together in one simple workflow.
        </p>

        <div className="space-y-12 pad:space-y-16 sm:space-y-24">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              title={feature.title}
              description={feature.description}
              imageUrl={feature.imageUrl}
              imageAlt={feature.imageAlt}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PowerfulFeaturesSection;
